import {
  ChangeDetectionStrategy,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  Type,
  ViewChild
} from '@angular/core';
import { LabelInflatorContainerDirective } from './label-inflator-container.directive';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ComputeLabelInterface } from '../label/compute-label-interface';
import { LabelStyle } from '../../../models/enum/shared/label-style.enum';
import { LabelComponent } from '../label/label-component';
import { BasicRoundBorderLabelComponent } from '../basic-round-border-label/basic-round-border-label.component';
import { DisplayLabelInterface } from '../label/display-label-interface';
import { Label } from '../../../models/shared/label';
import { DistinctUtils } from '../../../utils/distinct-utils';
import { exists } from '../../../functions/exists';

/**
 * Don't use multiple interfaces at a time. Component was designed to use one at a time.
 */
@Component({
  selector: 'app-label-inflator',
  templateUrl: './label-inflator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LabelInflatorComponent implements OnInit, OnChanges, OnDestroy {
  constructor(private componentFactoryResolver: ComponentFactoryResolver) {}

  @Input() computeLabelInterface: ComputeLabelInterface;
  @Input() displayLabelInterface: DisplayLabelInterface;
  @Input() zoom: number = 1;
  @Output() clearClicked = new EventEmitter<Label>(true);
  @ViewChild(LabelInflatorContainerDirective, { static: true }) contentContainer: LabelInflatorContainerDirective;

  private _currentLabelText = new BehaviorSubject<string>('');
  public computedLabelText$ = this._currentLabelText.pipe(distinctUntilChanged());

  private _currentLabelStyle = new BehaviorSubject<LabelStyle>(null);
  public currentLabelStyle$ = this._currentLabelStyle.pipe(distinctUntilChanged());

  private _computedPrimaryLabel = new BehaviorSubject<Label>(null);
  public computedPrimaryLabel$ = this._computedPrimaryLabel.pipe(
    distinctUntilChanged(DistinctUtils.distinctUniquelyIdentifiable)
  );

  private _computedSecondaryLabels = new BehaviorSubject<Label[]>(null);
  public computedSecondaryLabels$ = this._computedSecondaryLabels.pipe(
    distinctUntilChanged(DistinctUtils.distinctUniquelyIdentifiableArray)
  );

  private labelTextSub: Subscription;
  private clearClickedSub: Subscription;
  private primarySub: Subscription;
  private secSub: Subscription;

  private compInstance: LabelComponent;

  ngOnInit(): void {
    this.inflateLabel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const interfaceChanged = exists(changes.computeLabelInterface) || exists(changes.displayLabelInterface);
    if (interfaceChanged) this.inflateLabel();
    if (exists(this.compInstance)) {
      if (exists(changes.zoom)) this.compInstance.zoom = this.zoom;
      this.compInstance.ngOnChanges(changes);
    }
  }

  inflateLabel() {
    this._currentLabelStyle.next(LabelStyle.Default);
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory<LabelComponent>(
      this.getLabelComponentType()
    );
    this.contentContainer.viewContainerRef.clear();
    const componentRef = this.contentContainer.viewContainerRef.createComponent(componentFactory);
    this.compInstance = componentRef.instance as LabelComponent;
    this.passThroughInputs();
    this.passThroughOutputs();
  }

  private passThroughInputs(): void {
    this.compInstance.displayLabelInterface = this.displayLabelInterface;
    this.compInstance.zoom = this.zoom;
  }

  private passThroughOutputs(): void {
    this.labelTextSub?.unsubscribe();
    this.labelTextSub = this.compInstance.viewModel.labelText$.subscribe(text => this._currentLabelText.next(text));
    this.clearClickedSub?.unsubscribe();
    this.clearClickedSub = this.compInstance.clearClicked$.subscribe(clear => this.clearClicked.emit(clear));
    this.primarySub?.unsubscribe();
    this.primarySub = this.compInstance.viewModel.label$
      .pipe(debounceTime(1))
      .subscribe(l => this._computedPrimaryLabel.next(l));
  }

  private getLabelComponentType(): Type<LabelComponent> {
    return BasicRoundBorderLabelComponent;
  }

  ngOnDestroy(): void {
    this.labelTextSub?.unsubscribe();
    this.clearClickedSub?.unsubscribe();
    this.primarySub?.unsubscribe();
    this.secSub?.unsubscribe();
  }
}
